"use client";

import React from "react";
import { MapPin, Phone, Mail, Clock, CircleCheck, Loader, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { cn } from "@/lib/utils";
function JourneyForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in zoom-in duration-500">
        <div className="size-16 rounded-full bg-green-100 flex items-center justify-center">
          <CircleCheck className="size-8 text-green-600" />
        </div>
        <h3 className="text-2xl font-serif font-semibold text-primary">Request Received</h3>
        <p className="text-muted-foreground max-w-xs mx-auto">
          {successMessage}
        </p>
        <Button variant="outline" className="mt-4" onClick={() => window.location.reload()}>
          Start Another Inquiry
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="full_name" rules={{
        required: "Full Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Full Name</FormLabel>
              <FormControl>
                <Input placeholder="John Doe" {...field} className="bg-muted/30 border-input/60 focus:bg-background transition-colors" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" {...field} className="bg-muted/30 border-input/60 focus:bg-background transition-colors" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="phone" rules={{
        required: "Phone Number is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="(555) 123-4567" {...field} className="bg-muted/30 border-input/60 focus:bg-background transition-colors" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="budget" render={({
        field
      }) => <FormItem>
              <FormLabel>Budget / Price Range</FormLabel>
              <FormControl>
                <Input placeholder="e.g., $300,000 - $500,000" {...field} className="bg-muted/30 border-input/60 focus:bg-background transition-colors" />
              </FormControl>
              <FormDescription className="text-xs">
                Provide an approximate budget to help us match suitable properties.
              </FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="property_type" rules={{
        required: "Property Type is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Property Type</FormLabel>
              <FormControl>
                <div className="relative">
                  <select className={cn("flex h-9 w-full items-center justify-between rounded-md border border-input bg-muted/30 px-3 py-2 text-sm shadow-xs ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50 appearance-none transition-colors focus:bg-background")} {...field}>
                    <option value="" disabled>Select Type</option>
                    {["Buy", "Rent", "Invest"].map((opt, i) => <option key={opt} value={opt} data-index={i}>
                        {opt}
                      </option>)}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-muted-foreground">
                    <svg className="h-4 w-4 fill-current" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                      <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                    </svg>
                  </div>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="timeline" render={({
        field
      }) => <FormItem>
              <FormLabel>Desired Move-in Timeline</FormLabel>
              <FormControl>
                <div className="relative">
                  <select className={cn("flex h-9 w-full items-center justify-between rounded-md border border-input bg-muted/30 px-3 py-2 text-sm shadow-xs ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50 appearance-none transition-colors focus:bg-background")} {...field}>
                    <option value="" disabled>Select Timeline</option>
                    {["Immediately", "Within 3 months", "3-6 months", "6+ months"].map((opt, i) => <option key={opt} value={opt} data-index={i}>
                        {opt}
                      </option>)}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-muted-foreground">
                    <svg className="h-4 w-4 fill-current" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                      <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                    </svg>
                  </div>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      {submitError && <div className="p-3 text-sm text-destructive bg-destructive/10 rounded-md border border-destructive/20">
          {submitError}
        </div>}

      <Button type="submit" variant="secondary" size="lg" className="w-full mt-6 font-semibold text-secondary-foreground shadow-md hover:shadow-lg transition-all duration-300" disabled={isSubmitting}>Find a home<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="lucide lucide-arrow-right ml-2 h-4 w-4" aria-hidden="true" data-ast-id="f:/src/pages/ReachOut/sections/StartYourJourney.tsx|sig:xiqe8" data-ast-start="7436" data-ast-end="7475"><path d="M5 12h14"></path><path d="m12 5 7 7-7 7"></path></svg></Button>
    </Form>;
}
export default function StartYourJourney() {
  return <section id="start-your-journey" className="py-20 bg-background relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none opacity-30">
        <div className="absolute -top-[20%] -left-[10%] w-[50%] h-[50%] rounded-full bg-secondary/5 blur-3xl" />
        <div className="absolute top-[40%] -right-[10%] w-[40%] h-[40%] rounded-full bg-primary/5 blur-3xl" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          
          {}
          <div className="lg:col-span-7">
            <Card className="border-none shadow-xl bg-card/80 backdrop-blur-sm ring-1 ring-border/50">
              <CardHeader className="pb-2">
                <div className="flex items-center gap-2 mb-2">
                  <span className="h-px w-8 bg-secondary inline-block"></span>
                  <span className="text-secondary font-medium text-sm uppercase tracking-wider">Lets Get Started</span>
                </div>
                <CardTitle className="text-3xl md:text-4xl font-serif text-primary">Find a home</CardTitle>
                <CardDescription className="text-base mt-2">Specializing in first-time homebuyers across Orange County, and the surrounding Hudson Valley area. With deep knowledge of local school districts and community nuances, we're here to guide you to the perfect home.</CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                <FormProvider formId="start_journey_form_reach_out" sectionName="start-your-journey" defaultValues={{
                full_name: "",
                email: "",
                phone: "",
                budget: "",
                property_type: "",
                timeline: ""
              }} validationRules={{
                full_name: {
                  required: true
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                phone: {
                  required: true
                },
                property_type: {
                  required: true
                }
              }} submitText="Start Your Journey" successMessage="Thank you for reaching out! Chris will review your details and get back to you shortly to discuss the next steps." errorMessage="We couldn't send your request. Please check your connection and try again.">
                  <JourneyForm />
                </FormProvider>
              </CardContent>
            </Card>
          </div>

          {}
          <div className="lg:col-span-5 space-y-8 lg:sticky lg:top-24">
            
            {}
            <div className="rounded-xl overflow-hidden shadow-lg border border-border bg-card group">
              <div className="relative h-64 w-full bg-muted">
                <img src="https://findahomewithchris.com/wp-content/uploads/2026/03/Logo-with-toon.png" alt="Map of Orange County, New York" className="object-cover opacity-90 group-hover:opacity-100 transition-opacity duration-500 !w-[465px] !h-[152px]" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent pointer-events-none" />
                <div className="absolute bottom-4 left-4 text-white">
                  <div className="flex items-center gap-2 mb-1">
                    <MapPin className="h-4 w-4 text-secondary" />
                    <span className="font-medium text-sm">Orange County, NY</span>
                  </div>
                  <p className="text-xs text-white/80 pl-6">Serving the entire Hudson Valley region</p>
                </div>
              </div>
            </div>

            {}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {}
              <HoverCard>
                <HoverCardTrigger asChild>
                  <Button variant="outline" className="w-full h-auto flex items-center justify-start gap-4 p-4 rounded-xl bg-card border-border shadow-sm hover:shadow-md hover:border-secondary/50 hover:bg-card transition-all group whitespace-normal">
                    <div className="size-10 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary group-hover:text-white transition-colors text-primary shrink-0">
                      <Phone className="size-5" />
                    </div>
                    <div className="text-left">
                      <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Call me</p>
                      <p className="text-sm font-semibold text-foreground group-hover:text-primary transition-colors">(845)-787-6872</p>
                    </div>
                  </Button>
                </HoverCardTrigger>
                <HoverCardContent className="w-80">
                  <div className="flex justify-between space-x-4">
                    <div className="space-y-1">
                      <h4 className="text-sm font-semibold">Phone Support</h4>
                      <p className="text-sm text-muted-foreground">
                        Available Monday to Friday, 9am - 6pm.
                      </p>
                      <div className="flex items-center pt-2">
                        <Clock className="mr-2 h-3 w-3 opacity-70" /> 
                        <span className="text-xs text-muted-foreground">Typical response: Immediate</span>
                      </div>
                    </div>
                  </div>
                </HoverCardContent>
              </HoverCard>

              {}
              <HoverCard>
                <HoverCardTrigger asChild>
                  <Button variant="outline" className="w-full h-auto flex items-center justify-start gap-4 p-4 rounded-xl bg-card border-border shadow-sm hover:shadow-md hover:border-secondary/50 hover:bg-card transition-all group whitespace-normal">
                    <div className="size-10 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary group-hover:text-white transition-colors text-primary shrink-0">
                      <Mail className="size-5" />
                    </div>
                    <div className="text-left overflow-hidden">
                      <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Email me</p>
                      <p className="text-sm font-semibold text-foreground group-hover:text-primary transition-colors truncate w-full">findahomewithchris@gmail.com</p>
                    </div>
                  </Button>
                </HoverCardTrigger>
                <HoverCardContent className="w-80">
                  <div className="flex justify-between space-x-4">
                    <div className="space-y-1">
                      <h4 className="text-sm font-semibold">Email Support</h4>
                      <p className="text-sm text-muted-foreground">
                        Send us your questions anytime.
                      </p>
                      <div className="flex items-center pt-2">
                        <Clock className="mr-2 h-3 w-3 opacity-70" /> 
                        <span className="text-xs text-muted-foreground">Typical response: Within 24 hours</span>
                      </div>
                    </div>
                  </div>
                </HoverCardContent>
              </HoverCard>
            </div>

            {}
            <div className="p-6 rounded-xl bg-muted/30 border border-border/50">
              <h4 className="font-serif text-lg font-medium text-primary mb-3">Why work with Chris?</h4>
              <ul className="space-y-3">
                {["Deep local market knowledge of Orange County", "Personalized property matches based on your lifestyle", "Transparent communication every step of the way"].map((item, i) => <li key={i} data-index={i} className="flex items-start gap-3 text-sm text-muted-foreground">
                    <CircleCheck className="h-5 w-5 text-secondary shrink-0" />
                    <span>{item}</span>
                  </li>)}
              </ul>
            </div>

          </div>
        </div>
      </div>
    </section>;
}